import { Badge, Button, Card, Group, Stack, Text, ThemeIcon } from '@mantine/core'
import { IconBrandMastercard, IconBrandVisa, IconCreditCard, IconWallet } from '@tabler/icons-react'

import type { PaymentMethod } from '@/services/api/api.types'
import { isBankAccountPaymentMethod, isPaymentMethodVerified } from '@/utils/paymentMethodHelpers'

interface PaymentMethodDisplayProps {
  paymentMethod: PaymentMethod
  onUpdate?: () => void
  onRemove?: () => void
  showActions?: boolean
}

export function PaymentMethodDisplay({
  paymentMethod,
  onUpdate,
  onRemove,
  showActions = true,
}: PaymentMethodDisplayProps) {
  const getPaymentMethodIcon = (paymentMethod: PaymentMethod) => {
    // For card-based payment methods, use brand-specific icons
    if (paymentMethod.brand) {
      switch (paymentMethod.brand.toLowerCase()) {
        case 'visa':
          return <IconBrandVisa size={30} />
        case 'mastercard':
          return <IconBrandMastercard size={30} />
        default:
          return <IconCreditCard size={30} />
      }
    }

    // For non-card payment methods, use generic wallet icon
    return <IconWallet size={30} />
  }

  const formatExpiryDate = (month: number, year: number) => {
    return `${month.toString().padStart(2, '0')}/${year.toString().slice(-2)}`
  }

  const getPaymentMethodDisplayText = (paymentMethod: PaymentMethod) => {
    // For card-based payment methods with last4
    if (paymentMethod.last4) {
      return `•••• ${paymentMethod.last4}`
    }

    // For other payment method types, show the type with proper formatting
    return paymentMethod.type.charAt(0).toUpperCase() + paymentMethod.type.slice(1)
  }

  const getExpiryDisplayText = (paymentMethod: PaymentMethod) => {
    if (paymentMethod.expMonth && paymentMethod.expYear) {
      return `Expires ${formatExpiryDate(paymentMethod.expMonth, paymentMethod.expYear)}`
    }
    return null
  }

  const handleUpdate = () => {
    if (onUpdate) {
      onUpdate()
    }
  }

  const handleRemove = () => {
    if (onRemove) {
      onRemove()
    }
  }

  return (
    <Card withBorder p="md" radius="md" style={{ width: 'fit-content' }}>
      <Stack gap="md">
        <Group gap="md" align="center" pb="md">
          <ThemeIcon variant="light" size="xl" color="gray">
            {getPaymentMethodIcon(paymentMethod)}
          </ThemeIcon>
          <Text size="md" fw={500} pr="xl">
            {getPaymentMethodDisplayText(paymentMethod)}
          </Text>
          {getExpiryDisplayText(paymentMethod) && (
            <Text size="md" c="dimmed">
              {getExpiryDisplayText(paymentMethod)}
            </Text>
          )}
          {isBankAccountPaymentMethod(paymentMethod) &&
            (isPaymentMethodVerified(paymentMethod) ? (
              <Badge color="green" variant="light">
                Verified
              </Badge>
            ) : (
              <Badge color="yellow" variant="light">
                Verification pending
              </Badge>
            ))}
        </Group>

        {showActions && (
          <Group gap="sm" justify="flex-end">
            {onUpdate && (
              <Button variant="light" size="sm" onClick={handleUpdate}>
                Change
              </Button>
            )}
            {onRemove && (
              <Button variant="light" color="red" size="sm" onClick={handleRemove}>
                Delete
              </Button>
            )}
          </Group>
        )}
      </Stack>
    </Card>
  )
}
